import { WorkItem } from '../../../../../../types/Work/WorkItem'
import { ReworkTask, WorkItemTask } from '../../../../../../types/Work/WorkItemTask'

/** `path` names the work item and those above it, as the selected tasks list shows them. */
export type WorkItemsTabRow = { id: string; workItem: WorkItem; layer: number } & (
	| { kind: 'workItem' | 'loading' | 'error' }
	| { kind: 'task'; task: WorkItemTask; path: string[] }
	| { kind: 'rework'; rework: ReworkTask; path: string[] }
)

export const hasWorkItemChildren = (workItem: WorkItem) =>
	workItem.childrenWorkItems ? workItem.childrenWorkItems.length > 0 : !!workItem.hasChildren

/** Each open work item is followed by its tasks and their reworks, then its children. */
export function getVisibleWorkItemsTabRows(
	workItems: WorkItem[],
	childrenById: Map<number, WorkItem[]>,
	expandedIds: Set<number>,
	loadingIds: Set<number>,
	failedIds: Set<number>,
): WorkItemsTabRow[] {
	const rows: WorkItemsTabRow[] = []
	const visit = (
		workItem: WorkItem,
		layer: number,
		parentRowId: string,
		parentPath: string[],
		ancestors: Set<number>,
	) => {
		// Search results can repeat a work item inside another match, so ids include the path.
		const id = `${parentRowId}/wi-${workItem.id}`
		rows.push({ id, kind: 'workItem', workItem, layer })
		if (!expandedIds.has(workItem.id) || ancestors.has(workItem.id)) return
		const path = [...parentPath, workItem.name]
		for (const task of workItem.tasks ?? []) {
			rows.push({ id: `${id}/task-${task.id}`, kind: 'task', workItem, layer, task, path })
			for (const rework of task.reworks ?? []) {
				rows.push({ id: `${id}/rework-${rework.id}`, kind: 'rework', workItem, layer, rework, path })
			}
		}
		const children = workItem.childrenWorkItems ?? childrenById.get(workItem.id)
		if (children) {
			const childAncestors = new Set(ancestors).add(workItem.id)
			children.forEach((child) => visit(child, layer + 1, id, path, childAncestors))
		} else if (loadingIds.has(workItem.id)) {
			rows.push({ id: `${id}/loading`, kind: 'loading', workItem, layer })
		} else if (failedIds.has(workItem.id)) {
			rows.push({ id: `${id}/error`, kind: 'error', workItem, layer })
		}
	}
	workItems.forEach((workItem) => visit(workItem, 0, '', [], new Set()))
	return rows
}
